/**
 * @typedef {{ id: string, page: number, x0: number, y0: number, x1: number, y1: number, label?: string, categoria?: string, sospetto?: boolean }} PdfBBox
 */

function newBBoxId() {
  if (typeof crypto !== "undefined" && crypto.randomUUID) return crypto.randomUUID();
  return `bbox-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

function num(v) {
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

function rectFromItem(item) {
  let page = num(item.page ?? item.page_num ?? item.pageNumber);
  let x0 = num(item.x0);
  let y0 = num(item.y0);
  let x1 = num(item.x1);
  let y1 = num(item.y1);
  let fromBoxCoord = false;

  if (Array.isArray(item.bbox) && item.bbox.length >= 4) {
    x0 = num(item.bbox[0]);
    y0 = num(item.bbox[1]);
    x1 = num(item.bbox[2]);
    y1 = num(item.bbox[3]);
    if (page == null && item.bbox.length >= 5) page = num(item.bbox[4]);
  }

  if (Array.isArray(item.box_coord) && item.box_coord.length >= 4) {
    const xs = item.box_coord.map((pt) => (Array.isArray(pt) ? num(pt[0]) : null)).filter((n) => n != null);
    const ys = item.box_coord.map((pt) => (Array.isArray(pt) ? num(pt[1]) : null)).filter((n) => n != null);
    if (xs.length >= 2 && ys.length >= 2) {
      x0 = Math.min(...xs);
      x1 = Math.max(...xs);
      y0 = Math.min(...ys);
      y1 = Math.max(...ys);
      fromBoxCoord = true;
    }
  }

  if (page == null || x0 == null || y0 == null || x1 == null || y1 == null) return null;

  return {
    page: Math.max(1, Math.round(page)),
    x0: Math.min(x0, x1),
    y0: Math.min(y0, y1),
    x1: Math.max(x0, x1),
    y1: Math.max(y0, y1),
    fromBoxCoord,
    alreadyPdf: !fromBoxCoord && item.coord_space === "pdf",
  };
}

/**
 * NER `box_coord` values are top-left image coords (same scale as the page, y down).
 * PDF.js expects PDF user space (origin bottom-left).
 */
function topLeftRectToPdfRect(rect, pageBox, srcW, srcH) {
  const [xMin, , xMax, yMax] = pageBox;
  const pageW = xMax - pageBox[0];
  const pageH = yMax - pageBox[1];
  const scaleX = srcW > 0 ? pageW / srcW : 1;
  const scaleY = srcH > 0 ? pageH / srcH : 1;

  const ix0 = rect.x0 * scaleX;
  const ix1 = rect.x1 * scaleX;
  const iy0 = rect.y0 * scaleY;
  const iy1 = rect.y1 * scaleY;

  return {
    x0: xMin + ix0,
    x1: xMin + ix1,
    y0: yMax - iy1,
    y1: yMax - iy0,
  };
}

/**
 * Normalize API / NER bbox shapes into editor PdfBBox list (PDF user space).
 * @param {import('pdfjs-dist').PDFDocumentProxy} doc
 * @param {unknown[]} incoming
 * @returns {Promise<PdfBBox[]>}
 */
export async function normalizeIncomingBboxes(doc, incoming) {
  if (!Array.isArray(incoming) || !doc) return [];

  const parsed = [];
  for (const raw of incoming) {
    if (!raw || typeof raw !== "object") continue;
    const rect = rectFromItem(/** @type {Record<string, unknown>} */ (raw));
    if (!rect) continue;
    parsed.push({ raw, rect });
  }

  const pageMeta = new Map();
  const ensurePageMeta = async (pageNum) => {
    if (pageMeta.has(pageNum)) return pageMeta.get(pageNum);
    const page = await doc.getPage(pageNum);
    const view = Array.isArray(page.view) && page.view.length === 4
      ? page.view
      : [0, 0, 612, 792];
    const vp = page.getViewport({ scale: 1 });
    const meta = {
      view,
      width: vp.width,
      height: vp.height,
    };
    pageMeta.set(pageNum, meta);
    return meta;
  };

  const srcExtents = new Map();
  for (const { rect } of parsed) {
    if (!rect.fromBoxCoord) continue;
    const prev = srcExtents.get(rect.page) || { maxX: 0, maxY: 0 };
    prev.maxX = Math.max(prev.maxX, rect.x1);
    prev.maxY = Math.max(prev.maxY, rect.y1);
    srcExtents.set(rect.page, prev);
  }

  const out = [];
  for (const { raw, rect } of parsed) {
    const item = /** @type {Record<string, unknown>} */ (raw);
    const meta = await ensurePageMeta(rect.page);
    const [xMin, yMin, xMax, yMax] = meta.view;
    const pageW = xMax - xMin;
    const pageH = yMax - yMin;

    let pdfRect;
    if (rect.alreadyPdf) {
      pdfRect = { x0: rect.x0, y0: rect.y0, x1: rect.x1, y1: rect.y1 };
    } else if (rect.fromBoxCoord) {
      const src = srcExtents.get(rect.page) || { maxX: rect.x1, maxY: rect.y1 };
      const srcW = src.maxX > pageW * 1.02 ? src.maxX : pageW;
      const srcH = src.maxY > pageH * 1.02 ? src.maxY : pageH;
      pdfRect = topLeftRectToPdfRect(rect, meta.view, srcW, srcH);
    } else if (rect.y1 <= pageH * 1.05 && rect.y0 >= 0 && rect.x1 <= pageW * 1.05) {
      // Heuristic: looks like top-left OCR coords without box_coord wrapper.
      pdfRect = topLeftRectToPdfRect(rect, meta.view, pageW, pageH);
    } else {
      pdfRect = { x0: rect.x0, y0: rect.y0, x1: rect.x1, y1: rect.y1 };
    }

    const label = typeof item.label === "string" ? item.label
      : typeof item.entity === "string" ? item.entity
      : typeof item.category === "string" ? item.category
      : typeof item.text === "string" ? item.text
      : undefined;

    // Terzo stato. `categoria` viaggia a parte da `label`: label finisce in
    // `text` nel payload di /redact (vedi buildRedactPayload), quindi riusarla
    // per la categoria romperebbe il round-trip del testo originale.
    const categoria = typeof item.categoria === "string" ? item.categoria
      : typeof item.category === "string" ? item.category
      : undefined;

    out.push({
      id: typeof item.id === "string" ? item.id : newBBoxId(),
      page: rect.page,
      x0: pdfRect.x0,
      y0: pdfRect.y0,
      x1: pdfRect.x1,
      y1: pdfRect.y1,
      ...(label ? { label } : {}),
      ...(categoria ? { categoria } : {}),
      // L'arbitro suggerisce una rilettura: la parola resta oscurata, il
      // frontend la disegna solo con un contrassegno in piu'.
      ...(item.sospetto === true ? { sospetto: true } : {}),
      // Segnalazione gia' guardata dall'utente: viaggia con le bbox di
      // /redact e torna da GET /editor, cosi' dopo un'anonimizzazione non
      // ricompare in coda (vedi confermatiDalServer).
      ...(item.controllato === true ? { controllato: true } : {}),
    });
  }

  return out;
}

/**
 * Raggruppa le parole segnalate adiacenti in UNA segnalazione.
 *
 * Il backend manda una bbox per parola, quindi "Mario Rossi" arriva come due
 * bbox entrambe sospette. Per l'utente e' una cosa sola: va etichettata una
 * volta, contata una volta e liberata con un gesto solo. Stessa regola di
 * `group_redacted_entities` lato server — parole consecutive, stessa pagina,
 * stessa categoria — con in piu' il vincolo di riga, che qui serve perche'
 * ragioniamo su geometria e non su indici di parola.
 *
 * Le occorrenze ripetute NON si fondono: il report le deduplica per
 * placeholder, ma nell'editor ogni occorrenza e' un punto diverso della pagina
 * da guardare.
 *
 * @param {PdfBBox[]} bboxes
 * @returns {{id: string, page: number, categoria?: string, x0: number, y0: number, x1: number, y1: number, items: PdfBBox[]}[]}
 */
export function groupSospetti(bboxes) {
  const flagged = (Array.isArray(bboxes) ? bboxes : []).filter((b) => b.sospetto === true);
  // Ordine di lettura: pagina, poi riga dall'alto (in spazio PDF la y cresce
  // verso l'alto), poi da sinistra.
  const sorted = [...flagged].sort(
    (a, b) => a.page - b.page || b.y1 - a.y1 || a.x0 - b.x0,
  );

  const runs = [];
  for (const b of sorted) {
    const run = runs[runs.length - 1];
    const prev = run && run.items[run.items.length - 1];
    const h = Math.max(1, b.y1 - b.y0);
    const gap = prev ? b.x0 - prev.x1 : Infinity;

    const contiguo = prev
      && prev.page === b.page
      && (prev.categoria || "") === (b.categoria || "")
      // stessa riga: i due box si sovrappongono in verticale
      && Math.abs(prev.y0 - b.y0) < h * 0.5
      && Math.abs(prev.y1 - b.y1) < h * 0.5
      // attaccati: al massimo uno spazio tipografico fra le due parole
      && gap <= h * 0.6
      && gap >= -h;

    if (contiguo) {
      run.items.push(b);
      run.x0 = Math.min(run.x0, b.x0);
      run.y0 = Math.min(run.y0, b.y0);
      run.x1 = Math.max(run.x1, b.x1);
      run.y1 = Math.max(run.y1, b.y1);
    } else {
      runs.push({
        // l'id della prima parola: ancora stabile per selezione e key React
        id: b.id,
        page: b.page,
        categoria: b.categoria,
        x0: b.x0,
        y0: b.y0,
        x1: b.x1,
        y1: b.y1,
        items: [b],
      });
    }
  }
  return runs;
}

/** PDF user space (bottom-left origin) → NER/OCR top-left image coords. */
function pdfRectToTopLeftRect(pdfRect, pageView) {
  const [xMin, , , yMax] = pageView;
  return {
    x0: pdfRect.x0 - xMin,
    y0: yMax - pdfRect.y1,
    x1: pdfRect.x1 - xMin,
    y1: yMax - pdfRect.y0,
  };
}

function topLeftRectToBoxCoord(rect) {
  const x0 = Math.min(rect.x0, rect.x1);
  const x1 = Math.max(rect.x0, rect.x1);
  const y0 = Math.min(rect.y0, rect.y1);
  const y1 = Math.max(rect.y0, rect.y1);
  return [
    [x0, y0],
    [x1, y0],
    [x1, y1],
    [x0, y1],
  ];
}

/**
 * Convert editor bboxes (PDF.js user space) to the NER /redact payload shape.
 * @param {import('pdfjs-dist').PDFDocumentProxy} doc
 * @param {PdfBBox[]} bboxes
 * @param {ReadonlySet<string>} [controllati] id delle parole segnalate che
 *   l'utente ha gia' guardato: partono con `controllato`, e il motore le
 *   rimanda cosi' (i campi in piu' tornano identici)
 * @returns {Promise<object[]>}
 */
export async function buildRedactPayload(doc, bboxes, controllati) {
  if (!doc || !Array.isArray(bboxes)) return [];

  const pageMeta = new Map();
  const ensurePageMeta = async (pageNum) => {
    const n = Math.max(1, Math.round(pageNum));
    if (pageMeta.has(n)) return pageMeta.get(n);
    const page = await doc.getPage(n);
    const view = Array.isArray(page.view) && page.view.length === 4
      ? page.view
      : [0, 0, 612, 792];
    pageMeta.set(n, { view });
    return pageMeta.get(n);
  };

  const out = [];
  for (const bbox of bboxes) {
    const meta = await ensurePageMeta(bbox.page);
    const tl = pdfRectToTopLeftRect(bbox, meta.view);
    const item = {
      page: Math.max(1, Math.round(bbox.page)),
      box_coord: topLeftRectToBoxCoord(tl),
      x0: tl.x0,
      y0: tl.y0,
      x1: tl.x1,
      y1: tl.y1,
    };
    if (bbox.label) item.text = bbox.label;
    // Campi additivi: /redact li accetta senza lamentarsi e rimandarli
    // indietro tiene la bbox identica a come e' arrivata.
    if (bbox.categoria) item.categoria = bbox.categoria;
    if (bbox.sospetto) item.sospetto = true;
    if (bbox.sospetto && controllati?.has(bbox.id)) item.controllato = true;
    out.push(item);
  }
  return out;
}

/**
 * Le segnalazioni che il server rimanda come gia' controllate: gli id dei
 * gruppi le cui parole portano tutte `controllato`. Sono i "confermati" con
 * cui ripartire quando la revisione locale non c'e' (dopo un'anonimizzazione,
 * o dopo un aggiornamento della pagina).
 * @param {PdfBBox[]} bboxes
 * @returns {string[]}
 */
export function confermatiDalServer(bboxes) {
  return groupSospetti(bboxes)
    .filter((r) => r.items.every((w) => w.controllato === true))
    .map((r) => r.id);
}

/** file_index within a multi-file job for a UI results row. */
export function fileIndexForResult(results, rowIndex) {
  const jobId = results[rowIndex]?.obscura_job_id;
  let idx = 0;
  for (let i = 0; i < rowIndex; i += 1) {
    if (results[i]?.obscura_job_id === jobId) idx += 1;
  }
  return idx;
}

export function isPdfResult(row) {
  const name = row?.redacted_filename || row?.original_filename || "";
  return /\.pdf$/i.test(name);
}

/* ── Revisione in corso: cosa si salva, e cosa si riprende ──────────────────
 *
 * L'editor viene smontato ogni volta che si cambia tab o documento, quindi la
 * revisione va tenuta fuori dal componente. Le due regole stanno qui, separate
 * dalla resa a schermo, perche' sbagliarle non produce un errore: produce un
 * documento che sembra a posto e non lo e'.
 */

/**
 * Cosa vale la pena tenere da parte quando l'editor si smonta.
 *
 * Una lista vuota NON e' una revisione: e' l'assenza di revisione. Succede
 * subito dopo un "Anonimizza" riuscito, quando i riquadri sono ormai impressi
 * nel documento. Salvarla coprirebbe la copia che il server intanto ha
 * registrato, e al rientro l'editor mostrerebbe l'originale pulito.
 *
 * @param {object[]} bboxes riquadri correnti
 * @param {Iterable<string>} confermati identificativi gia' guardati dall'utente
 * @returns {{bboxes: object[], confermati: string[]}|null}
 */
export function statoDaSalvare(bboxes, confermati, modificato = false) {
  if (!bboxes?.length) return null;
  return { bboxes, confermati: [...(confermati || [])], modificato: Boolean(modificato) };
}

/**
 * Vero se le due liste descrivono le stesse aree: stessi id e stessa
 * geometria, a prescindere dall'ordine. Serve a capire se l'utente ha
 * cambiato qualcosa rispetto a quello che il server ha gia' applicato.
 */
export function stesseAree(a, b) {
  const x = Array.isArray(a) ? a : [];
  const y = Array.isArray(b) ? b : [];
  if (x.length !== y.length) return false;
  const impronta = (r) => [r?.id, r?.page, r?.x0, r?.y0, r?.x1, r?.y1]
    .map((v) => (typeof v === "number" ? Math.round(v * 100) / 100 : String(v ?? "")))
    .join("|");
  const diA = x.map(impronta).sort();
  const diB = y.map(impronta).sort();
  return diA.every((v, i) => v === diB[i]);
}

/**
 * Da cosa ripartire quando l'editor si riapre.
 *
 * Vince la revisione locale solo se contiene qualcosa; altrimenti valgono le
 * aree che arrivano dal server, che dopo un'anonimizzazione riuscita sono
 * proprio quelle appena applicate (POST /redact le scrive nel job).
 *
 * I contrassegni "gia' guardato" seguono i riquadri a cui appartengono: se si
 * riparte da quelli del server, gli identificativi locali non corrispondono
 * piu' e valgono solo quelli che il server stesso rimanda come controllati
 * (`controllato` sulle parole, messo da buildRedactPayload). Prima si
 * azzeravano e basta, e dopo ogni "Anonimizza" le segnalazioni gia' viste
 * tornavano tutte in coda.
 *
 * @param {{bboxes?: object[], confermati?: string[]}|null|undefined} salvato
 * @param {object[]} dalServer
 */
export function statoDaRipristinare(salvato, dalServer) {
  const ripreso = Boolean(salvato?.bboxes?.length);
  return {
    ripreso,
    bboxes: ripreso ? salvato.bboxes : dalServer || [],
    confermati: ripreso ? salvato.confermati || [] : confermatiDalServer(dalServer || []),
  };
}
